import React, { useState, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
const QuillEditor: any = ReactQuill;
import 'react-quill-new/dist/quill.snow.css';
import { Eye, Code, Save, Layout, Calculator, Database, Plus, Trash2, FileText, FileSpreadsheet } from 'lucide-react';

const SECTION_TABS = [
    { id: 'header', label: 'Header Section', icon: Layout },
    { id: 'body',   label: 'Body / Table Grid', icon: Database },
    { id: 'footer', label: 'Footer & Formulas', icon: Calculator },
];

const FORMULA_TYPES = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX'];

interface TemplateEditorProps {
    template?: any;
    savedQueries?: any[];
    user?: any;
    onSave: (templateData: any) => void;
    onCancel: () => void;
    showToast?: (msg: string) => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, savedQueries = [], user, onSave, onCancel, showToast }) => {
    const [activeTemplate, setActiveTemplate] = useState<any>(() => {
        const init = template || { name: '', type: 'PDF', templateCategory: 'query' };
        return {
            ...init,
            sections: template?.sections || {
                header: { content: template?.content || '', mode: 'rich' },
                body: {
                    includeTable: true,
                    selectedColumns: [],
                    groupByColumn: '',
                    sortColumn: '',
                    sortDirection: 'ASC',
                    content: '',
                    mode: 'rich'
                },
                footer: {
                    content: '',
                    formulas: [],
                    mode: 'rich'
                }
            }
        };
    });

    const [activeTab, setActiveTab] = useState('header');
    const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'
    const [newFormula, setNewFormula] = useState({ label: '', type: 'SUM', column: '' });
    const quillRef = useRef(null);

    const updateSectionContent = (content) => {
        setActiveTemplate(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                [activeTab]: {
                    ...prev.sections[activeTab],
                    content
                }
            }
        }));
    };

    const handleAddFormula = () => {
        if (!newFormula.label || !newFormula.column) {
            if (showToast) showToast('Please enter both label and column name', 'error');
            return;
        }
        const currentFormulas = activeTemplate.sections?.footer?.formulas || [];
        setActiveTemplate(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                footer: {
                    ...prev.sections.footer,
                    formulas: [...currentFormulas, { ...newFormula }]
                }
            }
        }));
        setNewFormula({ label: '', type: 'SUM', column: '' });
        if (showToast) showToast('Formula added!');
    };

    const handleRemoveFormula = (index) => {
        const currentFormulas = activeTemplate.sections?.footer?.formulas || [];
        setActiveTemplate(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                footer: {
                    ...prev.sections.footer,
                    formulas: currentFormulas.filter((_, i) => i !== index)
                }
            }
        }));
    };

    const selectedQuery = useMemo(() => {
        if (!activeTemplate.queryId || !savedQueries.length) return null;
        return savedQueries.find(q => q.id === activeTemplate.queryId);
    }, [activeTemplate.queryId, savedQueries]);

    return (
        <div className="max-w-6xl mx-auto h-full flex flex-col bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-lg">
            {/* Header Bar */}
            <div className="bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <input
                        type="text"
                        placeholder="Query Template Name..."
                        value={activeTemplate.name}
                        onChange={e => setActiveTemplate(prev => ({ ...prev, name: e.target.value }))}
                        className="text-lg font-bold bg-transparent border-b border-dashed border-slate-300 dark:border-slate-600 focus:border-indigo-500 focus:outline-none text-slate-800 dark:text-slate-100"
                    />
                    <select
                        value={activeTemplate.type || 'PDF'}
                        onChange={e => setActiveTemplate(prev => ({ ...prev, type: e.target.value }))}
                        className="text-xs bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded px-2.5 py-1 text-slate-700 dark:text-slate-200"
                    >
                        <option value="PDF">PDF Output</option>
                        <option value="HTML">HTML Web Output</option>
                        <option value="EXCEL">Excel Export Layout</option>
                    </select>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => setViewMode(viewMode === 'edit' ? 'preview' : 'edit')} className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 flex items-center gap-1.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700">
                        {viewMode === 'edit' ? <Eye size={14} /> : <Code size={14} />}
                        {viewMode === 'edit' ? 'Preview' : 'Editor'}
                    </button>
                    <button onClick={() => onSave(activeTemplate)} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs rounded-md shadow flex items-center gap-1.5 cursor-pointer">
                        <Save size={14} /> Save Template
                    </button>
                    <button onClick={onCancel} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md cursor-pointer">Cancel</button>
                </div>
            </div>

            {/* Section Tabs */}
            <div className="flex border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
                {SECTION_TABS.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                                activeTab === tab.id ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-slate-500'
                            }`}
                        >
                            <Icon size={14} /> {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Main Area */}
            <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 flex flex-col bg-white dark:bg-slate-800">
                    {activeTab === 'body' && (
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex flex-wrap gap-4 items-center text-xs">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-600 dark:text-slate-300">Linked Saved Query:</span>
                                <select
                                    value={activeTemplate.queryId || ''}
                                    onChange={e => setActiveTemplate(prev => ({ ...prev, queryId: e.target.value }))}
                                    className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-3 py-1 text-slate-700 dark:text-slate-200"
                                >
                                    <option value="">-- Select Saved Query --</option>
                                    {savedQueries.map(q => (
                                        <option key={q.id} value={q.id}>{q.name || q.id}</option>
                                    ))}
                                </select>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-300">
                                <input
                                    type="checkbox"
                                    checked={activeTemplate.sections?.body?.includeTable ?? true}
                                    onChange={e => setActiveTemplate(prev => ({
                                        ...prev,
                                        sections: {
                                            ...prev.sections,
                                            body: { ...prev.sections.body, includeTable: e.target.checked }
                                        }
                                    }))}
                                    className="rounded border-slate-300 text-indigo-600"
                                />
                                Include Data Table Grid
                            </label>
                        </div>
                    )}

                    {viewMode === 'edit' ? (
                        <QuillEditor
                            ref={quillRef}
                            theme="snow"
                            value={activeTemplate.sections[activeTab]?.content || ''}
                            onChange={updateSectionContent}
                            className="flex-1 flex flex-col"
                        />
                    ) : (
                        <div className="flex-1 p-8 bg-white dark:bg-slate-900 overflow-y-auto">
                            <div className="prose dark:prose-invert max-w-none bg-white dark:bg-slate-800 p-8 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 min-h-[400px]">
                                <div dangerouslySetInnerHTML={{ __html: activeTemplate.sections?.header?.content || '' }} />
                                <div className="my-6" dangerouslySetInnerHTML={{ __html: activeTemplate.sections?.body?.content || '' }} />
                                {activeTemplate.sections?.body?.includeTable && (
                                    <div className="my-4 p-4 border border-dashed border-indigo-300 dark:border-indigo-700 rounded bg-indigo-50/50 dark:bg-indigo-950/20 text-center text-xs text-indigo-600 dark:text-indigo-400 font-mono">
                                        [ Data Table Grid Linked to Query: {selectedQuery?.name || activeTemplate.queryId || 'Default Query'} ]
                                    </div>
                                )}
                                <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700" dangerouslySetInnerHTML={{ __html: activeTemplate.sections?.footer?.content || '' }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar for Footer Formulas */}
                {activeTab === 'footer' && (
                    <div className="w-72 bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 p-4 overflow-y-auto">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                            <Calculator size={14} /> Report Formulas
                        </h4>

                        <div className="space-y-2 mb-4 bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700 text-xs">
                            <input
                                type="text"
                                placeholder="Label (e.g. Total Revenue)"
                                value={newFormula.label}
                                onChange={e => setNewFormula(prev => ({ ...prev, label: e.target.value }))}
                                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded p-1.5"
                            />
                            <div className="flex gap-2">
                                <select
                                    value={newFormula.type}
                                    onChange={e => setNewFormula(prev => ({ ...prev, type: e.target.value }))}
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded p-1.5 flex-1"
                                >
                                    {FORMULA_TYPES.map(ft => <option key={ft} value={ft}>{ft}</option>)}
                                </select>
                                <input
                                    type="text"
                                    placeholder="Column Name"
                                    value={newFormula.column}
                                    onChange={e => setNewFormula(prev => ({ ...prev, column: e.target.value }))}
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded p-1.5 flex-1"
                                />
                            </div>
                            <button
                                onClick={handleAddFormula}
                                className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-1.5 rounded flex items-center justify-center gap-1 cursor-pointer"
                            >
                                <Plus size={14} /> Add Formula
                            </button>
                        </div>

                        <div className="space-y-1.5">
                            {(activeTemplate.sections?.footer?.formulas || []).map((f, idx) => (
                                <div key={idx} className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs">
                                    <div>
                                        <p className="font-semibold text-slate-800 dark:text-slate-200">{f.label}</p>
                                        <p className="text-[10px] text-slate-400 font-mono">{f.type}({f.column})</p>
                                    </div>
                                    <button onClick={() => handleRemoveFormula(idx)} className="text-red-500 hover:text-red-700 p-1 cursor-pointer">
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateEditor;
